import { Component, ReactNode } from "react";
import { logError } from "@hub/shared/utils/errorLogger";

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  /** Name used for error logging context. Defaults to "ErrorBoundary" */
  name?: string;
}

interface State {
  hasError: boolean;
  error?: Error;
}

/**
 * Error boundary component that catches React rendering errors.
 *
 * Wraps child components and displays a fallback UI if an error occurs
 * during rendering. Logs errors using the centralized error logger.
 *
 * Usage:
 *   <ErrorBoundary>
 *     <YourComponent />
 *   </ErrorBoundary>
 *
 *   // With custom fallback:
 *   <ErrorBoundary fallback={<CustomErrorUI />}>
 *     <YourComponent />
 *   </ErrorBoundary>
 *
 *   // With named context for logging:
 *   <ErrorBoundary name="PipelinesModule">
 *     <PipelinesRoutes />
 *   </ErrorBoundary>
 */
export class ErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false };

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    const context = this.props.name || "ErrorBoundary";
    logError(context, error);
    // Log React-specific error info for debugging
    if (info.componentStack) {
      console.error("React component stack:", info.componentStack);
    }
  }

  handleRetry = () => {
    this.setState({ hasError: false, error: undefined });
  };

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <div className="flex flex-col items-center justify-center min-h-screen bg-gray-50 dark:bg-slate-900">
          <div className="text-center max-w-md p-8">
            <div className="mb-6">
              <svg
                className="mx-auto h-16 w-16 text-red-500"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1.5}
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                />
              </svg>
            </div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-2">
              Something went wrong
            </h1>
            <p className="text-gray-600 dark:text-gray-400 mb-4">
              An unexpected error occurred while rendering this page.
            </p>
            {this.state.error?.message && (
              <p className="text-sm text-gray-500 dark:text-gray-500 mb-6 font-mono bg-gray-100 dark:bg-slate-800 p-3 rounded-md break-words">
                {this.state.error.message}
              </p>
            )}
            <button
              onClick={this.handleRetry}
              className="px-6 py-2.5 bg-primary-600 hover:bg-primary-700 text-white font-medium rounded-lg transition-colors focus:outline-hidden focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 dark:focus:ring-offset-slate-900"
            >
              Try again
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

interface ModuleErrorBoundaryProps {
  children: ReactNode;
  /** Module name for display and logging. Required. */
  moduleName: string;
}

interface ModuleErrorBoundaryState {
  hasError: boolean;
  error?: Error;
}

/**
 * Module-level error boundary that displays an inline error UI.
 *
 * Use this for wrapping individual route modules so that errors in one
 * module don't crash the entire app. Shows a smaller, inline error UI
 * that allows users to navigate to other parts of the app.
 *
 * Usage:
 *   <ModuleErrorBoundary moduleName="Pipelines">
 *     <PipelinesRoutes />
 *   </ModuleErrorBoundary>
 */
export class ModuleErrorBoundary extends Component<
  ModuleErrorBoundaryProps,
  ModuleErrorBoundaryState
> {
  state: ModuleErrorBoundaryState = { hasError: false };

  static getDerivedStateFromError(error: Error): ModuleErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    logError(`ModuleErrorBoundary.${this.props.moduleName}`, error);
    if (info.componentStack) {
      console.error("React component stack:", info.componentStack);
    }
  }

  handleRetry = () => {
    this.setState({ hasError: false, error: undefined });
  };

  render() {
    if (this.state.hasError) {
      return (
        <div className="p-6">
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-6">
            <div className="flex items-start">
              <div className="shrink-0">
                <svg
                  className="h-6 w-6 text-red-500"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                  />
                </svg>
              </div>
              <div className="ml-4 flex-1">
                <h3 className="text-lg font-medium text-red-800 dark:text-red-200">
                  Error loading {this.props.moduleName}
                </h3>
                <p className="mt-1 text-sm text-red-700 dark:text-red-300">
                  An unexpected error occurred while rendering this module.
                </p>
                {this.state.error?.message && (
                  <p className="mt-2 text-sm font-mono text-red-600 dark:text-red-400 bg-red-100 dark:bg-red-900/30 p-2 rounded">
                    {this.state.error.message}
                  </p>
                )}
                <div className="mt-4">
                  <button
                    onClick={this.handleRetry}
                    className="inline-flex items-center px-4 py-2 text-sm font-medium text-red-700 dark:text-red-200 bg-red-100 dark:bg-red-800/50 border border-red-300 dark:border-red-700 rounded-md hover:bg-red-200 dark:hover:bg-red-800 focus:outline-hidden focus:ring-2 focus:ring-red-500 focus:ring-offset-2 dark:focus:ring-offset-slate-900 transition-colors"
                  >
                    <svg
                      className="mr-2 h-4 w-4"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                      />
                    </svg>
                    Try again
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
